/**
 * Tour de registrar pago (apoderado): comprobante → monto → asignación → confirmar.
 * No exige registrar un pago real.
 */
const paymentsRegisterIntro = {
    id: "payments.register-intro",
    version: 3,
    title: "Registrar un pago",
    description: "Te guiamos para informar un abono con comprobante, monto y asignación a fondos.",
    entryRouteName: "mis-pagos",
    persistDismissal: false,
    steps: [
        {
            id: "open-register",
            target: "payments-register",
            routeName: "mis-pagos",
            title: "Informa tu pago",
            text: "Toca «Registrar abono» para abrir el formulario.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.skipMisPagosStep !== true,
            advanceOn: {
                event: "payment:register_clicked",
            },
        },
        {
            id: "intro-cuotas",
            target: "payments-register-root-cuotas",
            routeName: "abonar-cobros-cuotas",
            title: "Informar un abono",
            text: "Subes el comprobante, confirmas el monto y lo repartes entre tus cobros pendientes.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.skipMisPagosStep === true &&
                context.treasuryType !== "centro_costos",
        },
        {
            id: "intro-cc",
            target: "payments-register-wizard-cc",
            routeName: "registrar-abono-centro-costo",
            title: "Informar un abono",
            text: "Son tres pasos: comprobante, monto y asignación a los fondos del curso.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.skipMisPagosStep === true &&
                context.treasuryType === "centro_costos",
        },
        {
            id: "upload-cuotas",
            target: "payments-upload-receipt",
            routeName: "abonar-cobros-cuotas",
            title: "Sube tu comprobante",
            text: "Arrastra, pega o toca para adjuntar foto o PDF. Debe verse el monto de la transferencia.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.treasuryType !== "centro_costos",
            advanceOn: {
                event: "payment:step_receipt_done",
            },
        },
        {
            id: "upload-cc",
            target: "payments-upload-receipt",
            routeName: "registrar-abono-centro-costo",
            title: "Sube tu comprobante",
            text: "Arrastra, pega o toca para adjuntar foto o PDF. Debe verse el monto de la transferencia.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.treasuryType === "centro_costos",
            advanceOn: {
                event: "payment:step_receipt_done",
            },
        },
        {
            id: "amount-cuotas",
            target: "payments-register-amount-cuotas",
            routeName: "abonar-cobros-cuotas",
            title: "Revisa el monto",
            text: "Confirma que el monto detectado coincide con tu transferencia. Corrígelo si hace falta y toca «Continuar».",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.treasuryType !== "centro_costos",
            advanceOn: {
                event: "payment:step_amount_done",
            },
        },
        {
            id: "amount-cc",
            target: "payments-register-amount-cc",
            routeName: "registrar-abono-centro-costo",
            title: "Revisa el monto",
            text: "Confirma que el monto detectado coincide con tu comprobante. Corrígelo si hace falta y toca «Continuar».",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.treasuryType === "centro_costos",
            advanceOn: {
                event: "payment:step_amount_done",
            },
        },
        {
            id: "allocation-cuotas",
            target: "payments-register-allocation-cuotas",
            routeName: "abonar-cobros-cuotas",
            title: "Asigna y confirma",
            text: "Reparte el monto entre tus cobros. Cuando el restante sea $0, toca «Registrar abono». El tesorero validará el comprobante. No hace falta registrar ahora para cerrar la guía.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.treasuryType !== "centro_costos",
        },
        {
            id: "allocation-cc",
            target: "payments-register-allocation-cc",
            routeName: "registrar-abono-centro-costo",
            title: "Asigna y confirma",
            text: "Toca cada fondo para repartir el monto. Luego «Confirmar abono». No hace falta registrar ahora para cerrar la guía.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.treasuryType === "centro_costos",
        },
    ],
};
/**
 * Tour de creación de fondo. Ramifica según el tipo elegido (mismo monto / colecta / distintos).
 */
const fundsParadigmIntro = {
    id: "funds.paradigm-intro",
    version: 6,
    title: "Crear tu primer fondo",
    description: "Te guiamos para crear un fondo: conocerás los 3 tipos y practicarás el que elijas.",
    entryRouteName: "gestion-centro-costos",
    persistDismissal: false,
    steps: [
        {
            id: "explain-fund",
            target: "funds-empty-intro",
            routeName: "gestion-centro-costos",
            title: "Un fondo = una meta del curso",
            text: "Un fondo junta plata para un propósito del año (paseo, regalo, actividad). Idealmente ya lo acordaron en reunión.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasNoFunds === true,
        },
        {
            id: "open-wizard",
            target: "funds-add-fab",
            routeName: "gestion-centro-costos",
            title: "Crea tu primer fondo",
            text: "Toca el botón + brillante abajo a la derecha para abrir la creación.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "fund:create_clicked",
            },
        },
        {
            id: "name",
            target: "funds-wizard-name",
            routeName: "gestion-centro-costos",
            title: "Nómbralo por el propósito",
            text: "Usa el nombre de la actividad, no «Cuota marzo». Ejemplo: «Huevos de Pascua». Luego toca Continuar en el formulario.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "fund:wizard_named",
            },
        },
        {
            id: "explain-modes",
            target: "funds-wizard-modes",
            routeName: "gestion-centro-costos",
            title: "Tres formas de recaudar",
            text: "💵 Mismo monto — misma meta para todas las familias.\n\n🤝 Colecta — cada uno aporta lo que pueda.\n\n📊 Montos distintos — meta distinta por apoderado (Excel).\n\nToca la tarjeta del tipo que quieres usar. Luego te explicamos ese fondo.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => !context.fundMode,
            advanceOn: {
                event: "fund:wizard_mode_selected",
            },
        },
        {
            id: "explain-mode-same",
            target: "funds-wizard-continue",
            routeName: "gestion-centro-costos",
            title: "Mismo monto para todos",
            text: "Todas las familias tienen la misma meta. Un fondo es una actividad del año, no una cuota mensual: no crees «Cuota septiembre Navidad» y otro «Cuota octubre Navidad».\n\nSi Navidad son $10.000 al mes por 4 meses, crea UN fondo de $40.000. Cada familia va abonando lo que pueda hasta completar esa meta.\n\nCuando lo tengas claro, toca Continuar.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.fundMode === "cuota_igual",
            advanceOn: {
                event: "fund:wizard_mode_chosen",
            },
        },
        {
            id: "explain-mode-colecta",
            target: "funds-wizard-continue",
            routeName: "gestion-centro-costos",
            title: "Colecta (aporte libre)",
            text: "No hay cuota fija: cada familia aporta lo que pueda, cuando pueda. Sirve para una meta del curso (un regalo, una actividad), no para cobros mes a mes.\n\nTampoco armes un fondo por septiembre y otro por octubre: un solo fondo de Navidad (o de la actividad) y que vayan abonando.\n\nCuando lo tengas claro, toca Continuar.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.fundMode === "colecta",
            advanceOn: {
                event: "fund:wizard_mode_chosen",
            },
        },
        {
            id: "explain-mode-distintos",
            target: "funds-wizard-continue",
            routeName: "gestion-centro-costos",
            title: "Montos distintos por apoderado",
            text: "Cada familia tiene su propia meta; después las cargas con Excel. Sigue siendo UN fondo por actividad, no una cuota de marzo y otra de abril.\n\nSi a una familia le toca $10.000 al mes hasta Navidad (4 meses), su meta en el Excel es $40.000 en este único fondo. Van pagando con abonos hasta llegar a su monto.\n\nCuando lo tengas claro, toca Continuar.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.fundMode === "distintos",
            advanceOn: {
                event: "fund:wizard_mode_chosen",
            },
        },
        {
            id: "payment-shape",
            target: "funds-wizard-payment",
            routeName: "gestion-centro-costos",
            title: "Un pago o varios abonos",
            text: "En ambos casos se puede abonar más de una vez. Elige una opción en el formulario.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "fund:wizard_payment_set",
            },
        },
        {
            id: "dates",
            target: "funds-wizard-dates",
            routeName: "gestion-centro-costos",
            title: "Define la(s) fecha(s)",
            text: "Con un solo pago basta la fecha de término. Con varios abonos: inicio y término. Luego Continuar.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "fund:wizard_dates_set",
            },
        },
        {
            id: "amount",
            target: "funds-wizard-amount",
            routeName: "gestion-centro-costos",
            title: "Meta por apoderado",
            text: "Ejemplo: 6000. Aunque sea «un solo pago», pueden completar esa meta en varios abonos. Luego Continuar.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.fundMode === "cuota_igual",
            advanceOn: {
                event: "fund:wizard_amount_set",
            },
        },
        {
            id: "colecta-amount",
            target: "funds-wizard-colecta-amount",
            routeName: "gestion-centro-costos",
            title: "Monto sugerido (colecta)",
            text: "Puedes indicar un monto sugerido u omitirlo. Luego Continuar en el formulario.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.fundMode === "colecta",
            advanceOn: {
                event: "fund:wizard_colecta_amount_set",
            },
        },
        {
            id: "colecta-obligatoriedad",
            target: "funds-wizard-colecta-obligatoriedad",
            routeName: "gestion-centro-costos",
            title: "¿Participación obligatoria?",
            text: "Indica si todos deben aportar o si la colecta es voluntaria. Elige una opción.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.fundMode === "colecta",
            advanceOn: {
                event: "fund:wizard_colecta_obligatoriedad_set",
            },
        },
        {
            id: "excel-info",
            target: "funds-wizard-excel-info",
            routeName: "gestion-centro-costos",
            title: "Metas con Excel",
            text: "Tras crear el fondo subirás un Excel con el monto de cada apoderado. Revisa la información y Continuar.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.fundMode === "distintos",
            advanceOn: {
                event: "fund:wizard_excel_info_set",
            },
        },
        {
            id: "review",
            target: "funds-wizard-summary",
            routeName: "gestion-centro-costos",
            title: "Revisa el resumen",
            text: "Confirma nombre, modo, fechas y montos. Si todo está bien, toca Siguiente.",
            presentation: "popover",
            allowSkip: false,
        },
        {
            id: "create",
            target: "funds-wizard-create",
            routeName: "gestion-centro-costos",
            title: "Crea el fondo",
            text: "Toca Crear fondo. Después podrás recibir abonos, gastos e ingresos.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "fund:wizard_created",
            },
        },
    ],
};
/**
 * Tour de gestión de ventas (tesorero): crear → borrador → publicar → operar.
 * Flujo de compra del apoderado va en otra guía.
 */
const salesManageIntro = {
    id: "sales.manage-intro",
    version: 3,
    title: "Gestionar una campaña de venta",
    description: "Te guiamos para crear una campaña, publicarla y entender cómo confirmar pagos y entregas.",
    entryRouteName: "gestion-ventas",
    persistDismissal: false,
    steps: [
        {
            id: "explain-empty",
            target: "sales-manage-empty",
            routeName: "gestion-ventas",
            title: "Aquí organizas las ventas del curso",
            text: "Sirve para campañas con un objetivo (ej. venta de completos). Crearás productos, publicarás y luego confirmarás pagos y entregas.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasNoSaleCampaigns === true,
        },
        {
            id: "open-create",
            target: "sales-manage-create",
            routeName: "gestion-ventas",
            title: "Crea tu campaña",
            text: "Toca «Crear campaña de venta» para definir lo que verán los apoderados. Si ya hay campañas, aparecen agrupadas en En curso, Borradores y Finalizadas.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "sale:create_clicked",
            },
        },
        {
            id: "general-info",
            target: "sales-editor-title",
            routeName: "crear-evento-venta",
            title: "Datos que verán al comprar",
            text: "Completa título, propósito, fechas, instrucciones de pago, datos bancarios y lugar de entrega. Luego toca la sección de productos.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "sale:editor_general_done",
            },
        },
        {
            id: "products",
            target: "sales-editor-products",
            routeName: "crear-evento-venta",
            title: "Define los productos",
            text: "Nombre, precio y stock (o stock ilimitado). Puedes agregar más con «Agregar producto». Luego toca «Guardar borrador».",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "sale:editor_products_ready",
            },
        },
        {
            id: "save-draft",
            target: "sales-editor-save",
            routeName: "crear-evento-venta",
            title: "Guarda como borrador",
            text: "Revisa todo y toca «Guardar borrador». Aún no estará visible para los apoderados.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "sale:draft_saved",
            },
        },
        {
            id: "publish",
            target: "sales-admin-publish",
            routeName: "admin-evento-venta",
            title: "Publícala para el curso",
            text: "Con «Publicar», la campaña queda disponible para que los apoderados compren.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "sale:published",
            },
        },
        {
            id: "overview",
            target: "sales-admin-summary",
            routeName: "admin-evento-venta",
            title: "Sigue lo recaudado",
            text: "Aquí verás montos y participación. Toca la pestaña «Pedidos» para ver cómo gestionar los pagos.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "sale:admin_pedidos_tab_opened",
            },
        },
        {
            id: "orders-ops",
            target: "sales-admin-orders-tab",
            routeName: "admin-evento-venta",
            title: "Efectivo y entrega",
            text: "En Pedidos: «Confirmar efectivo» cuando recibas el dinero, y luego «Entregado» solo cuando ya entregaste el pedido.",
            presentation: "popover",
            allowSkip: false,
        },
        {
            id: "close",
            target: "sales-admin-close",
            routeName: "admin-evento-venta",
            title: "Cerrar cuando corresponda",
            text: "«Cerrar campaña» impide nuevas compras. Los pedidos ya hechos igual deben entregarse. No hace falta cerrarla ahora.",
            presentation: "popover",
            allowSkip: false,
        },
    ],
};
/**
 * Tour de compra en ventas (apoderado): ver productos → confirmar pedido.
 */
const salesBuyIntro = {
    id: "sales.buy-intro",
    version: 6,
    title: "Comprar en una campaña de venta",
    description: "Te guiamos para abrir una campaña, elegir productos y confirmar tu pedido.",
    entryRouteName: "ventas-list",
    persistDismissal: false,
    steps: [
        {
            id: "explain-list",
            target: "sales-buy-list",
            routeName: "ventas-list",
            title: "Ventas en curso",
            text: "Aquí ves las ventas en curso del curso seleccionado. Toca «Comprar» para revisar los productos y armar tu pedido.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasActiveSaleCampaigns === true,
        },
        {
            id: "open-campaign",
            target: "sales-buy-open",
            routeName: "ventas-list",
            title: "Comprar",
            text: "Toca «Comprar» para entrar a la campaña. No abre la pantalla de administración.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "sale:buy_clicked",
            },
        },
        {
            id: "delivery-info",
            target: "sales-buy-info",
            routeName: "venta-detalle",
            title: "Revisa la información",
            text: "Fíjate en el lugar de entrega, datos de pago e instrucciones. Luego suma al menos un producto con +.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "sale:buy_qty_selected",
            },
        },
        {
            id: "pick-products",
            target: "sales-buy-products",
            routeName: "venta-detalle",
            title: "Elige cantidades",
            text: "Ajusta las cantidades que necesitas. Abajo verás el total a pagar. Luego toca el recuadro de medio de pago.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "sale:buy_payment_focused",
            },
        },
        {
            id: "payment",
            target: "sales-buy-payment",
            routeName: "venta-detalle",
            title: "Medio de pago",
            text: "Puedes pagar con transferencia (subiendo el comprobante ahora o después) o en efectivo. Elige la opción que usarás; si subes el comprobante ahora, adjunta el archivo antes de confirmar.",
            presentation: "popover",
            allowSkip: false,
        },
        {
            id: "confirm",
            target: "sales-buy-confirm",
            routeName: "venta-detalle",
            title: "Confirma tu reserva",
            text: "Revisa todo y toca «Confirmar reserva». Con eso queda tu pedido y termina la guía.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "sale:order_confirmed",
            },
        },
    ],
};
/**
 * Tour de donaciones (apoderado): ver necesidades y registrar un aporte.
 */
const donationsCommitIntro = {
    id: "donations.commit-intro",
    version: 4,
    title: "Aportar en una campaña de donación",
    description: "Te guiamos para abrir una campaña, ver qué se necesita y registrar tu aporte.",
    entryRouteName: "donaciones-list",
    persistDismissal: false,
    steps: [
        {
            id: "explain-list",
            target: "donations-buy-list",
            routeName: "donaciones-list",
            title: "Donaciones en curso",
            text: "Aquí ves las donaciones en curso del curso seleccionado. Toca «Comprometer aporte» para revisar qué se pide y registrar tu aporte.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasActiveDonationCampaigns === true,
        },
        {
            id: "open-campaign",
            target: "donations-buy-open",
            routeName: "donaciones-list",
            title: "Comprometer aporte",
            text: "Toca «Comprometer aporte» para entrar a la campaña. No abre la pantalla de administración.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasActiveDonationCampaigns === true,
            advanceOn: {
                event: "donation:needs_clicked",
            },
        },
        {
            id: "pick-items",
            target: "donations-buy-items",
            routeName: "donacion-detalle",
            title: "Elige qué aportar",
            text: "Revisa el lugar de entrega si aparece arriba. Luego usa + en al menos un ítem para indicar la cantidad.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasActiveDonationCampaigns === true,
            advanceOn: {
                event: "donation:qty_selected",
            },
        },
        {
            id: "confirm",
            target: "donations-buy-confirm",
            routeName: "donacion-detalle",
            title: "Confirma tu compromiso",
            text: "Si quieres, deja un comentario. Luego toca «Confirmar compromiso». Con eso termina la guía.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasActiveDonationCampaigns === true,
            advanceOn: {
                event: "donation:commitment_confirmed",
            },
        },
    ],
};
/**
 * Tour de gestión de donaciones (tesorero): crear → borrador → activar → recibir.
 */
const donationsManageIntro = {
    id: "donations.manage-intro",
    version: 2,
    title: "Gestionar una campaña de donación",
    description: "Te guiamos para crear una campaña, activarla y entender cómo marcar lo recibido.",
    entryRouteName: "gestion-donaciones",
    persistDismissal: false,
    steps: [
        {
            id: "explain-empty",
            target: "donations-manage-empty",
            routeName: "gestion-donaciones",
            title: "Aquí organizas las donaciones del curso",
            text: "Sirve para pedir aportes en especie (ej. alimentos o útiles). Crearás ítems, activarás la campaña y luego marcarás lo que vaya llegando.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasNoDonationCampaigns === true,
        },
        {
            id: "open-create",
            target: "donations-manage-create",
            routeName: "gestion-donaciones",
            title: "Crea tu campaña",
            text: "Toca «Crear campaña de donación» para definir lo que verán los apoderados. Si ya hay campañas, aparecen agrupadas en En curso, Borradores y Finalizadas.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "donation:create_clicked",
            },
        },
        {
            id: "general-info",
            target: "donations-editor-title",
            routeName: "crear-campana-donacion",
            title: "Datos de la campaña",
            text: "Completa título, propósito, fechas, lugar e instrucciones de entrega. Luego toca la sección de ítems.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "donation:editor_general_done",
            },
        },
        {
            id: "items",
            target: "donations-editor-items",
            routeName: "crear-campana-donacion",
            title: "Define qué se puede donar",
            text: "Nombre, unidad y cantidad necesaria (o ilimitada). Puedes agregar más ítems. Luego toca «Guardar borrador».",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "donation:editor_items_ready",
            },
        },
        {
            id: "save-draft",
            target: "donations-editor-save",
            routeName: "crear-campana-donacion",
            title: "Guarda como borrador",
            text: "Revisa todo y toca «Guardar borrador». Aún no estará visible para los apoderados.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "donation:draft_saved",
            },
        },
        {
            id: "publish",
            target: "donations-admin-publish",
            routeName: "admin-campana-donacion",
            title: "Actívala para el curso",
            text: "Con «Activar», la campaña queda disponible para que los apoderados comprometan donaciones.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "donation:published",
            },
        },
        {
            id: "overview",
            target: "donations-buy-tab-items",
            routeName: "admin-campana-donacion",
            title: "Sigue lo que falta",
            text: "Aquí ves cada ítem, lo comprometido y lo recibido. Toca la pestaña «Donantes» para gestionar las entregas.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "donation:tab_compromisos_opened",
            },
        },
        {
            id: "commitments-ops",
            target: "donations-buy-tab-compromisos",
            routeName: "admin-campana-donacion",
            title: "Marcar recibido",
            text: "Cuando te entreguen lo prometido, usa «Marcar recibido». No hace falta que haya compromisos ahora.",
            presentation: "popover",
            allowSkip: false,
        },
        {
            id: "close",
            target: "donations-admin-close",
            routeName: "admin-campana-donacion",
            title: "Cerrar cuando corresponda",
            text: "«Cerrar campaña» impide nuevos compromisos. Lo ya comprometido igual debe recibirse. No hace falta cerrarla ahora.",
            presentation: "popover",
            allowSkip: false,
        },
    ],
};
/**
 * Tour de link de invitación (directiva): duración → generar → compartir.
 * No exige crear un link real.
 */
const inviteLinkIntro = {
    id: "invite.link-intro",
    version: 1,
    title: "Link de invitación",
    description: "Te guiamos para crear un enlace e invitar apoderados al curso.",
    entryRouteName: "crear-link-asociacion-curso",
    persistDismissal: false,
    steps: [
        {
            id: "intro",
            target: "invite-link-card",
            routeName: "crear-link-asociacion-curso",
            title: "Invitar apoderados al curso",
            text: "Generas un enlace para compartir por WhatsApp u otro medio. Quien lo abre puede registrarse en el grupo del curso.",
            presentation: "popover",
            allowSkip: false,
        },
        {
            id: "duration",
            target: "invite-link-duration",
            routeName: "crear-link-asociacion-curso",
            title: "Válido por",
            text: "Elige cuántos días estará activo (1 a 15). Pasado ese plazo, el link deja de servir.",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "invite:duration_selected",
            },
            when: (context) => context.hasGeneratedInviteLink !== true,
        },
        {
            id: "generate",
            target: "invite-link-generate",
            routeName: "crear-link-asociacion-curso",
            title: "Genera el enlace",
            text: "Toca «Generar enlace de asociación». Confirmas y luego copias el link para compartirlo. No hace falta crearlo ahora para cerrar la guía.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasGeneratedInviteLink !== true,
        },
        {
            id: "copy",
            target: "invite-link-copy",
            routeName: "crear-link-asociacion-curso",
            title: "Comparte el link",
            text: "Usa «Copiar» y envíalo a quien deba unirse. El código también sirve si prefieres dictarlo.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasGeneratedInviteLink === true,
        },
    ],
};
/**
 * Tour de ver gastos del curso: resumen, detalle y filtros.
 * No exige registrar ni editar gastos.
 */
const expensesViewIntro = {
    id: "expenses.view-intro",
    version: 1,
    title: "Ver gastos",
    description: "Te mostramos cómo revisar los gastos del curso y entender cada registro.",
    entryRouteName: "ver-gastos",
    persistDismissal: false,
    steps: [
        {
            id: "intro",
            target: "expenses-view-root",
            routeName: "ver-gastos",
            title: "Transparencia de gastos",
            text: "Aquí ves en qué se ha usado la plata del curso. Cada gasto muestra monto, fondo, quién lo registró y sus comprobantes.",
            presentation: "popover",
            allowSkip: false,
        },
        {
            id: "empty",
            target: "expenses-view-empty",
            routeName: "ver-gastos",
            title: "Aún sin gastos",
            text: "Cuando la directiva registre un gasto, aparecerá en esta lista para que todos puedan revisarlo.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasNoExpenses === true,
        },
        {
            id: "summary",
            target: "expenses-view-summary",
            routeName: "ver-gastos",
            title: "Resumen arriba",
            text: "Total y cantidad de gastos. Con «Filtros» puedes acotar por estado, fondo u orden.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasNoExpenses !== true,
        },
        {
            id: "card",
            target: "expenses-view-first-card",
            routeName: "ver-gastos",
            title: "Detalle de cada gasto",
            text: "Descripción, monto, fecha y responsable. Los estados «Por rendir», «Por reembolsar» y «Cerrado» indican si falta respaldo o comprobante de pago.\n\n«Transferencia» y «Respaldo» abren los archivos adjuntos.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasNoExpenses !== true,
        },
        {
            id: "filters",
            target: "expenses-view-filter",
            routeName: "ver-gastos",
            title: "Filtrar la lista",
            text: "Toca «Filtros» para buscar por estado, fondo u orden. No hace falta aplicar ningún filtro ahora.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasNoExpenses !== true,
        },
        {
            id: "register-empty",
            target: "expenses-view-empty-action",
            routeName: "ver-gastos",
            title: "Registrar el primer gasto",
            text: "Como tesorero, «Registrar Primer Gasto» abre el formulario. No hace falta crear uno ahora para cerrar la guía.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasNoExpenses === true && context.canRegisterExpense === true,
        },
        {
            id: "register-fab",
            target: "expenses-view-fab",
            routeName: "ver-gastos",
            title: "Agregar un gasto",
            text: "Como tesorero, el botón + abre el formulario para registrar un gasto nuevo. No hace falta crear uno ahora.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasNoExpenses !== true && context.canRegisterExpense === true,
        },
    ],
};
/**
 * Tour de ver ingresos del curso: resumen, detalle y filtros.
 * No exige registrar ni editar ingresos.
 */
const incomesViewIntro = {
    id: "incomes.view-intro",
    version: 1,
    title: "Ver ingresos",
    description: "Te mostramos cómo revisar los ingresos extra del curso y entender cada registro.",
    entryRouteName: "ver-ingresos",
    persistDismissal: false,
    steps: [
        {
            id: "intro",
            target: "incomes-view-root",
            routeName: "ver-ingresos",
            title: "Ingresos extra del curso",
            text: "Aquí ves plata que entró fuera de los pagos normales de apoderados: donaciones, aportes u otros ingresos registrados por la directiva.",
            presentation: "popover",
            allowSkip: false,
        },
        {
            id: "empty",
            target: "incomes-view-empty",
            routeName: "ver-ingresos",
            title: "Aún sin ingresos",
            text: "Cuando se registre un ingreso extra, aparecerá aquí para que todos puedan revisarlo.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasNoIncomes === true,
        },
        {
            id: "summary",
            target: "incomes-view-summary",
            routeName: "ver-ingresos",
            title: "Resumen arriba",
            text: "Total y cantidad de ingresos. Con «Filtros» puedes acotar por fondo u orden.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasNoIncomes !== true,
        },
        {
            id: "card",
            target: "incomes-view-first-card",
            routeName: "ver-ingresos",
            title: "Detalle de cada ingreso",
            text: "Descripción, monto, fecha y quién lo registró. Si hay comprobante, «Transferencia» abre la foto o el PDF.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasNoIncomes !== true,
        },
        {
            id: "filters",
            target: "incomes-view-filter",
            routeName: "ver-ingresos",
            title: "Filtrar la lista",
            text: "Toca «Filtros» para buscar por fondo u orden. No hace falta aplicar ningún filtro ahora.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasNoIncomes !== true,
        },
        {
            id: "register-empty",
            target: "incomes-view-empty-action",
            routeName: "ver-ingresos",
            title: "Registrar el primer ingreso",
            text: "Como tesorero, «Registrar Primer Ingreso» abre el formulario. No hace falta crear uno ahora para cerrar la guía.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasNoIncomes === true && context.canRegisterIncome === true,
        },
        {
            id: "register-fab",
            target: "incomes-view-fab",
            routeName: "ver-ingresos",
            title: "Agregar un ingreso",
            text: "Como tesorero, el botón + abre el formulario para registrar un ingreso extra. No hace falta crear uno ahora.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasNoIncomes !== true && context.canRegisterIncome === true,
        },
    ],
};
/**
 * Tour de registrar ingreso extra (tesorero): datos → comprobante → registrar.
 * No exige guardar un ingreso real.
 */
const incomeRegisterIntro = {
    id: "income.register-intro",
    version: 1,
    title: "Registrar ingreso extra",
    description: "Te guiamos para registrar plata que entró al curso fuera de los pagos normales.",
    entryRouteName: "registrar-ingreso",
    persistDismissal: false,
    steps: [
        {
            id: "intro",
            target: "income-register-wizard",
            routeName: "registrar-ingreso",
            title: "Ingresos fuera de los pagos normales",
            text: "Sirve para donaciones, aportes voluntarios u otra plata que no venga de un abono informado por apoderados. Son dos pasos: datos y comprobante.",
            presentation: "popover",
            allowSkip: false,
        },
        {
            id: "data",
            target: "income-register-data",
            routeName: "registrar-ingreso",
            title: "Datos del ingreso",
            text: "Si aparece «Fondos», elige cuál corresponde. Luego describe de dónde viene la plata y el monto. Toca «Siguiente».",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "income:step_data_done",
            },
        },
        {
            id: "receipt",
            target: "income-register-receipt",
            routeName: "registrar-ingreso",
            title: "Comprobante de transferencia",
            text: "Adjunta foto o PDF del comprobante. Puedes arrastrar, pegar o tocar «Subir». No hace falta registrar ahora para cerrar la guía.",
            presentation: "popover",
            allowSkip: false,
        },
        {
            id: "register",
            target: "income-register-submit",
            routeName: "registrar-ingreso",
            title: "Confirma el registro",
            text: "Cuando todo esté listo, toca «Registrar Ingreso». Revisarás un resumen antes de guardar.",
            presentation: "popover",
            allowSkip: false,
        },
    ],
};
const paymentsValidateIntro = {
    id: "payments.validate-intro",
    version: 1,
    title: "Validar pagos",
    description: "Te guiamos para revisar comprobantes, aprobar o rechazar, y corregir si te equivocas.",
    entryRouteName: "validar-pagos",
    persistDismissal: false,
    steps: [
        {
            id: "intro",
            target: "payments-validate-tabs",
            routeName: "validar-pagos",
            title: "Revisar comprobantes",
            text: "Los apoderados informan un pago con foto o PDF. Tú contrastas el comprobante con el monto. Empiezas en «Pendientes».",
            presentation: "popover",
            allowSkip: false,
        },
        {
            id: "empty-pending",
            target: "payments-validate-empty",
            routeName: "validar-pagos",
            title: "Nada por revisar",
            text: "Ahora mismo no hay pagos para revisar. Cuando alguien registre un abono, el comprobante aparece aquí.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasPendingPayments !== true,
        },
        {
            id: "review",
            target: "payments-validate-review",
            routeName: "validar-pagos",
            title: "Contrasta el comprobante",
            text: "Mira la imagen o el PDF, quién pagó y el monto. Si hay varios, las flechas cambian de pago. «Comprobante reutilizado» pide más cuidado.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasPendingPayments === true,
        },
        {
            id: "decide",
            target: "payments-validate-actions",
            routeName: "validar-pagos",
            title: "Aprobar o rechazar",
            text: "Si cuadra, «Aprobar». Si no, «Rechazar». Eso sí deja validado el pago: no hace falta que lo hagas ahora.",
            presentation: "popover",
            allowSkip: false,
            when: (context) => context.hasPendingPayments === true,
        },
        {
            id: "open-approved",
            target: "payments-validate-tab-approved",
            routeName: "validar-pagos",
            title: "El historial aprobado",
            text: "Toca la pestaña «Aprobados».",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "payment:tab_approved_opened",
            },
        },
        {
            id: "approved-history",
            target: "payments-validate-approved",
            routeName: "validar-pagos",
            title: "Ya aceptados",
            text: "Aquí ves lo que ya validaste. Si te equivocaste, «Cambiar a rechazado» (salvo que el dinero esté marcado como devuelto).",
            presentation: "popover",
            allowSkip: false,
        },
        {
            id: "open-rejected",
            target: "payments-validate-tab-rejected",
            routeName: "validar-pagos",
            title: "Los rechazados",
            text: "Toca la pestaña «Rechazados».",
            presentation: "popover",
            allowSkip: false,
            advanceOn: {
                event: "payment:tab_rejected_opened",
            },
        },
        {
            id: "rejected-history",
            target: "payments-validate-rejected",
            routeName: "validar-pagos",
            title: "Corregir un rechazo",
            text: "Si el apoderado envió un comprobante mejor, puedes «Cambiar a aprobado». Con eso conoces esta pantalla.",
            presentation: "popover",
            allowSkip: false,
        },
    ],
};
const guides = new Map([
    [paymentsRegisterIntro.id, paymentsRegisterIntro],
    [fundsParadigmIntro.id, fundsParadigmIntro],
    [salesManageIntro.id, salesManageIntro],
    [salesBuyIntro.id, salesBuyIntro],
    [donationsCommitIntro.id, donationsCommitIntro],
    [donationsManageIntro.id, donationsManageIntro],
    [paymentsValidateIntro.id, paymentsValidateIntro],
    [incomeRegisterIntro.id, incomeRegisterIntro],
    [inviteLinkIntro.id, inviteLinkIntro],
    [expensesViewIntro.id, expensesViewIntro],
    [incomesViewIntro.id, incomesViewIntro],
]);
/**
 * Mapa temporal route name → guide IDs disponibles en la pantalla.
 * En E9 evolucionará hacia recursos mixtos (guía + video).
 */
export const guidesByRoute = {
    "mis-pagos": ["payments.register-intro"],
    "abonar-cobros-cuotas": ["payments.register-intro"],
    "registrar-abono-centro-costo": ["payments.register-intro"],
    "gestion-centro-costos": ["funds.paradigm-intro"],
    "gestion-ventas": ["sales.manage-intro"],
    "ventas-list": ["sales.buy-intro"],
    "donaciones-list": ["donations.commit-intro"],
    "gestion-donaciones": ["donations.manage-intro"],
    "validar-pagos": ["payments.validate-intro"],
    "registrar-ingreso": ["income.register-intro"],
    "crear-link-asociacion-curso": ["invite.link-intro"],
    "ver-gastos": ["expenses.view-intro"],
    "ver-ingresos": ["incomes.view-intro"],
};
export const getGuideById = (guideId) => guides.get(guideId);
export const getGuidesForRoute = (routeName) => {
    const guideIds = guidesByRoute[routeName];
    if (!guideIds?.length)
        return [];
    return guideIds
        .map((id) => guides.get(id))
        .filter((guide) => Boolean(guide));
};
export const registerGuide = (guide) => {
    guides.set(guide.id, guide);
};
